import { readDevicePref } from './devicePrefs.js';

/**
 * Short vibrations for the moments a phone should answer back.
 *
 * Why this exists at all: on a phone the swipe deck is operated with a thumb,
 * often without looking closely at the screen — a card leaves the hand and
 * the next one is already there. A buzz is the cheapest possible confirmation
 * that the vote was taken, and the only one that survives a finger covering
 * half the card.
 *
 * Three rules shape the patterns below. They are short (nothing over 60ms,
 * so nothing feels like an alarm), they are *different* per answer (a No is a
 * double tap, so the three votes are distinguishable without looking), and a
 * match is the one event allowed to be louder than a vote, because it is the
 * one event everybody in the room reacts to.
 *
 * `navigator.vibrate` is Android and desktop Chrome; iOS Safari has never
 * implemented it and there is no polyfill worth the name — the Web Haptics
 * proposals are not shipping anywhere yet. So this degrades to nothing, and
 * the settings screen hides the switch where it would be a lie.
 */
export type HapticKind = 'like' | 'neutral' | 'dislike' | 'undo' | 'match' | 'error';

/** Milliseconds; odd positions are pauses, as `navigator.vibrate` reads them. */
export const HAPTIC_PATTERNS: Record<HapticKind, readonly number[]> = {
  // A yes is the loudest single pulse: it is also the one that interrupts
  // everybody else's deck.
  like: [24],
  neutral: [12],
  // Two short taps, so a no is recognisable through the pocket.
  dislike: [10, 40, 10],
  undo: [8, 30, 8],
  // The one celebratory pattern in the app.
  match: [30, 60, 30, 60, 60],
  error: [50],
};

type Vibrator = (pattern: number | number[]) => boolean;

function nativeVibrate(): Vibrator | null {
  if (typeof navigator === 'undefined') return null;
  const vibrate = (navigator as Navigator & { vibrate?: Vibrator }).vibrate;
  if (typeof vibrate !== 'function') return null;
  return vibrate.bind(navigator) as Vibrator;
}

/** Whether this device can vibrate at all — the settings switch asks first. */
export function hapticsSupported(): boolean {
  return nativeVibrate() !== null;
}

interface HapticOptions {
  /** Overrides the stored preference; only tests pass this. */
  enabled?: boolean;
  /** Overrides the platform vibrator; only tests pass this. */
  vibrate?: Vibrator | null;
}

/**
 * Fires one pattern, and returns whether it actually went out.
 *
 * Deliberately never throws: a vibration is feedback about something that has
 * already happened, so a browser that refuses one (an unsupported platform, a
 * tab without a user gesture behind it, a policy block) must not turn a cast
 * vote into an error.
 */
export function haptic(kind: HapticKind, options: HapticOptions = {}): boolean {
  const enabled = options.enabled ?? readDevicePref('haptics');
  if (!enabled) return false;

  const vibrate = options.vibrate !== undefined ? options.vibrate : nativeVibrate();
  if (!vibrate) return false;

  try {
    return vibrate([...HAPTIC_PATTERNS[kind]]);
  } catch {
    return false;
  }
}
